/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Checkbox, Popover} from "antd"
import {Icon} from "components/Icon"
import {BasePopup} from "components/popup"
import {BaseInput} from "components/inputs"
import {BaseButton, SecondaryButton} from "components/buttons"
import styles from "./DailyAttendancePopup.module.css"
import {attendanceService, profileService} from "services"
import {clone, cloneDeep, get, isEmpty} from "lodash"
import {convertTZ, formatDateOnly, getUserPhotoUrl, handleError, toastSuccess, toastWarning} from "helpers"
import moment from "moment-timezone"
import {BaseLoading} from "components"
import {useCurrentProfilePermissions, useModel, useVisible} from "hooks"
import {AttendanceEvent, AttendanceEventType, AttendanceStatus, AttendanceType} from "types/attendance"
import cx from "classnames"
import {BaseTimePicker} from "components/DateTimePicker"
import {v4 as uuid} from "uuid"
import {UserGeolocationMap} from "./UserGeolocationMap"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {ConfirmPopup} from "uiKit"
import {useMutation} from "@tanstack/react-query"
import {Permissions} from "@edular/permissions"
import {YesNo} from "types/common"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type Props = {
    isShow: boolean
    studentProfileIds?: number[]
    selectedAttendance?: any
    isExternshipAttendance?: boolean
    getListAttendances?: () => void
    onClose: () => void
}

export function DailyAttendancePopup(props: Props) {
    const {
        isShow,
        onClose,
        studentProfileIds = [],
        getListAttendances,
        selectedAttendance,
        isExternshipAttendance = false
    } = props
    const timezone = get(selectedAttendance, ["student", "timezone"], moment.tz.guess())
    const deleteEventIds = useRef<number[]>([])
    const {t} = useTranslation(["studentServices"])
    const [listEvent, setListEvent] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [showPopover, setShowPopover] = useState({})
    const [isVerified, setIsVerified] = useState(false)
    const [isMakeup, setIsMakeup] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isExcused, setIsExcused] = useState(false)
    const [studentInfo, setStudentInfo] = useState<any>()
    const [dateData, setDateData] = useState({
        checkInTime: "",
        checkOutTime: "",
        checkInDate: ""
    })
    const [geoLocation, setGeoLocation] = useState({latitude: null, longitude: null})
    const model = useModel()
    const timeFormat = model.getUserTimeFormat()
    const dateFormat = model.getUserDateFormat()
    const userGeolocationMapPopup = useVisible()
    const [isMapDisplayedAtLeastOnce, setIsMapDisplayedAtLeastOnce] = useState(false)
    const confirmDeletePopup = useVisible()

    const avatarUrl = studentInfo?.photo ? getUserPhotoUrl(studentInfo?.photo, 512) : "/image/DefaultAvatar.png"
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()

    const currentProfilePermissions = useCurrentProfilePermissions()

    const canEditAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
    })

    const canDeleteAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Delete]
    })

    const canAddAttendance = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    const deleteDailyAttendanceMutation = useMutation(attendanceService.deleteDailyAttendance, {
        onSuccess: () => {
            toastSuccess("Deleted successfully")
            getListAttendances()
            onClose()
        },
        onError: (err) => {
            handleError(err, true)
        }
    })

    const loading = isSubmitting || deleteDailyAttendanceMutation.isLoading

    const getStudentInfo = useCallback(async () => {
        const {data: student} = await profileService.getOne({
            id: studentProfileIds[0],
            type: "student"
        })
        setStudentInfo(student)
    }, [studentProfileIds])

    const getAttendanceDetail = useCallback(async () => {
        setIsLoading(true)
        try {
            const params = {
                attendanceIds: [selectedAttendance.id]
            }
            let data: any = []
            if (isExternshipAttendance) {
                data = [selectedAttendance]
            } else {
                const {data: attendanceData} = await attendanceService.getDailyAttendanceList({
                    range: {
                        page: 1,
                        pageSize: 1
                    },
                    ...params
                })
                data = attendanceData
            }
            if (data?.length) {
                const attendance = get(data, [0])
                const events = (attendance.events ?? []).sort(function (left, right) {
                    return moment.utc(left.createdAt).diff(moment.utc(right.createdAt))
                })
                const checkinEvent = events.find((item) => item.eventType === AttendanceEventType.Checkin)
                const checkoutEvent = events.find((item) => item.eventType === AttendanceEventType.Checkout)
                setDateData({
                    ...dateData,
                    checkInTime: checkinEvent
                        ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(timeFormat)
                        : "",
                    checkOutTime: checkoutEvent
                        ? moment(convertTZ(new Date(checkoutEvent.createdAt), timezone)).format(timeFormat)
                        : "",
                    checkInDate: checkinEvent
                        ? moment(convertTZ(new Date(checkinEvent.createdAt), timezone)).format(dateFormat)
                        : ""
                })
                setListEvent(
                    events.map((event) => {
                        return {
                            ...event,
                            createdAt: moment(event.createdAt).tz(timezone)
                        }
                    })
                )
            }
        } catch (err) {
            handleError(err)
        } finally {
            setIsLoading(false)
        }
    }, [studentProfileIds, selectedAttendance, timezone, isExternshipAttendance])

    useEffect(() => {
        studentProfileIds.length && getStudentInfo()
        if (isShow && selectedAttendance && studentProfileIds?.length && selectedAttendance?.attendanceId) {
            getAttendanceDetail()
            const isMakeup = !!selectedAttendance.isMakeup
            const status = selectedAttendance.status
            let isVerified = selectedAttendance.isVerified === YesNo.Yes
            if (status === AttendanceStatus.Approved && isVerified) {
                isVerified = true
            } else if (status === AttendanceStatus.Rejected && isVerified) {
                isVerified = false
            } else {
                isVerified = null
            }
            setIsVerified(isVerified)
            setIsMakeup(isMakeup)
            setIsExcused(isExcused)
        }
    }, [isShow, studentProfileIds, selectedAttendance])

    useEffect(() => {
        if (!isShow) {
            setStudentInfo(undefined)
            setListEvent([])
            deleteEventIds.current = []
        }
    }, [isShow])

    useEffect(() => {
        if (userGeolocationMapPopup.isVisible && !isMapDisplayedAtLeastOnce) {
            setIsMapDisplayedAtLeastOnce(true)
        }
    }, [userGeolocationMapPopup.isVisible, isMapDisplayedAtLeastOnce])

    const renderStudentInfo = () => {
        return (
            <div className={styles.studentWrapper}>
                <div className={styles.imgWrapper}>
                    <img src={avatarUrl} className={styles.avatar} alt="" />
                </div>
                <div>
                    <p className={styles.studentName}>{studentInfo?.fullName}</p>
                    <div className={styles.flexContainer}>
                        <div className={styles.columnContainer}>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.userId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customUserId}</span>
                            </div>
                            <div className={styles.labelContainer}>
                                <span className={styles.idLabel}>{t("popup.enrollmentId")}: </span>
                                <span className={styles.idValue}>{studentInfo?.customProfileId}</span>
                            </div>
                        </div>

                        <div className={styles.labelContainer}></div>
                    </div>
                </div>
            </div>
        )
    }

    const renderTitle = (eventType: string) => {
        if (eventType === "check-out") {
            return t("popup.checkout")
        } else if (eventType === "check-in") {
            return t("popup.checkin")
        } else if (eventType === "break-start") {
            return "Out"
        } else if (eventType === "break-end") {
            return "In"
        } else if (eventType === "lessonStart") {
            return "Lesson Start"
        } else if (eventType === "lessonEnd") {
            return "Lesson End"
        } else if (eventType === "attendance-check") {
            return "In"
        } else {
            return "Select Type"
        }
    }

    const renderIconAttendance = (eventType: string, status: string) => {
        if (eventType === "check-out") {
            return (
                <Icon
                    icon="CHECKOUT_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.checkOutIcon}
                />
            )
        } else if (eventType === "check-in") {
            return (
                <Icon
                    icon="CHECKIN_FILL"
                    color={status === "success" ? "#1E90FF" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else if (eventType === "break-start") {
            return <Icon icon="ATTENDANCE_OUT" color={"#DF1642"} className={styles.rightIcon} />
        } else if (eventType === "break-end") {
            return <Icon icon="ATTENDANCE_IN" color={"#18A957"} className={styles.rightIcon} />
        } else if (eventType === "attendance-check") {
            return (
                <Icon
                    icon="ATTENDANCE_IN"
                    color={status === "success" ? "#18A957" : "#E6E6E6"}
                    className={styles.rightIcon}
                />
            )
        } else {
            return <Icon icon="ARROW_DOWN" color={"grey"} className={styles.arrowIcon} />
        }
    }

    const onPressDeleteEvent = (event: AttendanceEvent) => {
        typeof event.id === "number" && deleteEventIds.current.push(event.id)
        const events = listEvent.filter((item) => item.id !== event.id)
        setListEvent([...events])
    }

    const onAddEvent = (e) => {
        let newListEvent = clone(listEvent)
        newListEvent.push({
            status: "success",
            id: uuid()
        })
        setListEvent(newListEvent)
    }

    const renderAddEvent = () => {
        return (
            <div className={styles.buttonAddEvent}>
                <BaseButton disabled={!canAddAttendance} title="Add Event" onClick={onAddEvent} />
            </div>
        )
    }

    const renderPopoverSelectEvent = (event: AttendanceEvent) => {
        const checkIn = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkin && item.status === "success"
        )
        const checkOut = listEvent.find(
            (item) => item.eventType === AttendanceEventType.Checkout && item.status === "success"
        )
        let options = cloneDeep(EVENT_OPTIONS)
        if (event?.eventType) {
            options = options.filter((item) => item.value !== event.eventType)
        }
        if (checkIn) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkin)
        }
        if (checkOut) {
            options = options.filter((item) => item.value !== AttendanceEventType.Checkout)
        }
        if (!isBreakEnabled) {
            options = options.filter(
                (item) => ![AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.value)
            )
        }

        if (isEmpty(options)) {
            return null
        }

        return (
            <div className={styles.selectContainer}>
                {options.map((option) => (
                    <div
                        onClick={() => {
                            event.eventType = option.value
                            setListEvent([...listEvent])
                            showPopover[event.id] = false
                            setShowPopover({...showPopover})
                        }}
                        className={cx(styles.popupContainer, styles.hoverBackgroundGrey)}>
                        {renderIconAttendance(option.value, "success")}
                        <span style={{width: 120}} className={styles.infoLabel}>
                            {renderTitle(option.value)}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    const renderCheckInItem = (data: any) => {
        const event = listEvent.find((item) => item.id === data.id)
        const isRenderDelete = event?.status === "success" && canEditAttendance
        const geoLocation = event?.jsonData?.geoLocation

        return (
            <div key={event?.id ?? uuid()}>
                <div className={styles.infoWrapper}>
                    {event?.status === "success" ? (
                        <>
                            <BaseTimePicker
                                style={{width: 130, marginRight: 24}}
                                value={data?.createdAt ? moment(convertTZ(new Date(data?.createdAt), timezone)) : ""}
                                format={timeFormat}
                                allowClear={false}
                                disabled={!!event?.id && !canEditAttendance}
                                onChange={(date) => {
                                    event.createdAt = moment(date).tz(timezone, true)
                                    setListEvent([...listEvent])
                                }}
                                placeholder="Time"
                            />
                        </>
                    ) : (
                        <div style={{backgroundColor: "transparent"}} className={styles.hourLabel} />
                    )}
                    {event?.status === "success" ? (
                        <Popover
                            placement="right"
                            color="white"
                            visible={showPopover[event.id]}
                            arrowContent={null}
                            onVisibleChange={(visible) => {
                                showPopover[event.id] = visible
                                setShowPopover({...showPopover})
                            }}
                            content={() => renderPopoverSelectEvent(event)}
                            trigger={!!event?.id && !canEditAttendance ? [] : "click"}>
                            <div className={styles.popupContainer}>
                                {renderIconAttendance(data?.eventType, data.status)}
                                <div className={styles.eventName}>
                                    <span
                                        style={{width: 80, color: data?.eventType ? "black" : "rgba(0,0,0,0.7)"}}
                                        className={styles.infoLabel}>
                                        {renderTitle(data?.eventType)}
                                    </span>
                                    {typeof data?.id === "number" ? (
                                        <div className={styles.eventInfo}>
                                            <span className={styles.eventId}>ID: {data.id} </span>
                                            {data.synced ? (
                                                <div className={styles.syncedIcon}>
                                                    <Icon icon="APPROVE" size={12} color={"var(--success-400-base)"} />
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </Popover>
                    ) : (
                        <div className={styles.eventName}>
                            <span style={{width: 80}} className={styles.infoLabel}>
                                {renderTitle(data?.eventType)}
                            </span>

                            {typeof data?.id === "number" ? (
                                <div className={styles.eventInfo}>
                                    <span className={styles.eventId}>ID: {data.id} </span>
                                    {data.synced ? (
                                        <div className={styles.syncedIcon}>
                                            <Icon icon="APPROVE" size={12} color={"var(--success-400-base)"} />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    )}
                    <div className={styles.applyRoundingContainer}>
                        <Checkbox
                            checked={event?.applyRounding}
                            disabled={!!event?.id && !canEditAttendance}
                            onChange={(e) => {
                                event.applyRounding = !event.applyRounding
                                setListEvent([...listEvent])
                            }}>
                            <span className={styles.infoLabel}>Apply Rounding</span>
                        </Checkbox>
                        {data?.createdByUserName?.length ? (
                            <span className={styles.eventId}>Created By: {data.createdByUserName} </span>
                        ) : null}
                    </div>
                    {event?.status === "success" ? (
                        <BaseInput
                            value={event?.message ?? ""}
                            onChange={(text) => {
                                event.message = text
                                setListEvent([...listEvent])
                            }}
                            disabled={!!event?.id && !canEditAttendance}
                            placeholder={t("popup.notes")}
                            className={styles.inputWidth}
                        />
                    ) : (
                        <div className={styles.inputWidth} />
                    )}
                    {!!geoLocation && (
                        <div
                            onClick={() => {
                                setGeoLocation(geoLocation)
                                userGeolocationMapPopup.open()
                            }}>
                            <Icon icon="LOCATION" className={styles.deleteIcon} />
                        </div>
                    )}
                    {isRenderDelete ? (
                        <div onClick={() => onPressDeleteEvent(event)}>
                            <Icon color={"var(--error-400-base)"} icon="DELETE" className={styles.deleteIcon} />
                        </div>
                    ) : null}
                </div>
            </div>
        )
    }

    const resetForm = useCallback(() => {
        getListAttendances?.()
        setStudentInfo(undefined)
        deleteEventIds.current = []
        setListEvent([])
        onClose()
    }, [getListAttendances])

    const onConfirmDeleteAttendance = useCallback(() => {
        const attendanceId = selectedAttendance.attendanceId

        deleteDailyAttendanceMutation.mutate({
            attendanceIds: [attendanceId]
        })
        confirmDeletePopup.close()
    }, [selectedAttendance])

    const onSave = useCallback(async () => {
        if (isExternshipAttendance) {
            onSaveExternshipAttendance()
            return
        }

        let listEventAdd = listEvent.filter((item) => typeof item.id !== "number")

        if (listEventAdd?.length && listEventAdd.find((event) => !event.eventType)) {
            return toastWarning("You need to select the event type: Check-in/Check-out or In/Out for all events")
        }

        if (listEventAdd?.length && listEventAdd.find((event) => !event.createdAt)) {
            return toastWarning("You need to select the time for all events")
        }

        setIsSubmitting(true)
        try {
            let status = selectedAttendance.status

            if (isVerified === true) {
                status = AttendanceStatus.Approved
            } else if (isVerified === false) {
                status = AttendanceStatus.Rejected
            }

            const params: any = {
                attendanceId: selectedAttendance.attendanceId,
                status: status,
                isVerified: [AttendanceStatus.Approved, AttendanceStatus.Rejected].includes(status) ? true : undefined,
                isMakeup: !!isMakeup
            }
            if (params.status === "approved") {
                params.isExcusedAbsence = false
            } else {
                params.isExcusedAbsence = !!isExcused
            }
            listEventAdd = listEventAdd.map((item) => {
                const createdAt = moment(item.createdAt).tz(timezone, true)
                const startDate = moment(selectedAttendance?.date).tz(timezone, true)
                createdAt.set({
                    month: startDate.month(),
                    date: startDate.date(),
                    year: startDate.year()
                })
                return {
                    message: item.message ?? "",
                    applyRounding: !!item.applyRounding,
                    eventType: item.eventType,
                    status: "success",
                    attendanceId: selectedAttendance.attendanceId,
                    createdAt: createdAt.utc().toISOString()
                }
            })

            const events = listEvent
                .filter((item) => typeof item.id === "number")
                .map((item) => {
                    const createdAt = moment(item.createdAt).tz(timezone, true)
                    const startDate = moment(selectedAttendance?.date).tz(timezone, true)
                    createdAt.set({
                        month: startDate.month(),
                        date: startDate.date(),
                        year: startDate.year()
                    })
                    return {
                        id: item.id,
                        applyRounding: !!item.applyRounding,
                        message: item.message ?? "",
                        eventType: item.eventType,
                        createdAt: createdAt.utc().toISOString()
                    }
                })
            if (events?.length) {
                params.events = events
            }
            if (deleteEventIds.current?.length) {
                params.deleteEventIds = deleteEventIds.current
            }
            if (listEventAdd.length) {
                params.addEvents = listEventAdd
            }
            await attendanceService.updateDailyAttendance(params)
            resetForm()
            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [resetForm, isVerified, selectedAttendance, isExcused, listEvent, timezone, isExternshipAttendance, isMakeup])

    const onSaveExternshipAttendance = useCallback(async () => {
        setIsSubmitting(true)
        try {
            const params: any = {
                attendanceId: selectedAttendance.attendanceId
            }
            let events = listEvent.map((item) => {
                const createdAt = moment(item.createdAt)
                const startDate = moment(convertTZ(new Date(selectedAttendance?.date), timezone))
                createdAt.set({
                    month: startDate.month(),
                    date: startDate.date(),
                    year: startDate.year()
                })
                return {
                    id: typeof item.id === "number" ? item.id : undefined,
                    message: item.message ?? null,
                    eventType: item.eventType,
                    attendanceType: item.attendanceType ?? AttendanceType.Manual,
                    status: item.status,
                    createdAt: createdAt.toISOString()
                }
            })
            let deletedEvents: any = []
            if (deleteEventIds.current?.length) {
                deletedEvents = deleteEventIds.current.map((x) => {
                    const deletedAt = moment()
                    const startDate = moment(convertTZ(new Date(selectedAttendance?.date), timezone))
                    deletedAt.set({
                        month: startDate.month(),
                        date: startDate.date(),
                        year: startDate.year()
                    })
                    return {
                        id: x,
                        deletedAt
                    }
                })
            }
            events = events.concat(deletedEvents)
            if (events?.length) {
                params.events = events
            }
            await attendanceService.updateExternshipDailyAttendance(params)
            resetForm()
            toastSuccess("Updated successfully")
        } catch (err) {
            handleError(err)
        } finally {
            setIsSubmitting(false)
        }
    }, [resetForm, isVerified, selectedAttendance, listEvent, timezone])

    const checkInEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkin && event.status === "success"
    )
    const checkOutEvent = listEvent.filter(
        (event) => event.eventType === AttendanceEventType.Checkout && event.status === "success"
    )
    const otherEvents = listEvent.filter(
        (event) => event.eventType !== AttendanceEventType.Checkout && event.eventType !== AttendanceEventType.Checkin
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="PERSON_FILL" width="70vw">
            <div className={styles.popupWrapper}>
                <p className={styles.title}>{t("attendance.title")}</p>
                {selectedAttendance?.date ? (
                    <div>
                        <div className={styles.timeWrapper}>
                            <span>{formatDateOnly(selectedAttendance?.date, dateFormat)}</span>
                        </div>
                    </div>
                ) : null}
                <hr className={styles.divisor} />
                {renderStudentInfo()}
                {selectedAttendance?.attendanceId ? (
                    <>
                        {checkInEvent.map((item) =>
                            renderCheckInItem({
                                status: "failure",
                                eventType: AttendanceEventType.Checkin,
                                ...item
                            })
                        )}
                        {otherEvents.map(renderCheckInItem)}
                        {checkOutEvent.map((item) =>
                            renderCheckInItem({
                                status: "failure",
                                eventType: AttendanceEventType.Checkout,
                                ...item
                            })
                        )}
                        {renderAddEvent()}
                        {!isExternshipAttendance && (
                            <>
                                <div className={styles.optionsWrap}>
                                    <div className={styles.approveAttendWrap}>
                                        <div className={styles.attendedBodyItem} onClick={() => setIsVerified(true)}>
                                            <span className={styles.checkLabel}>Approve</span>
                                            <div className={styles.unchecked}>
                                                <Icon
                                                    icon="APPROVE"
                                                    size={24}
                                                    color={isVerified === true ? "var(--success-400-base)" : "grey"}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.attendedBodyItem} onClick={() => setIsVerified(false)}>
                                            <span className={styles.checkLabel}>Reject</span>

                                            <div className={styles.unchecked}>
                                                <Icon
                                                    icon="CROSS_CIRCLE"
                                                    size={24}
                                                    color={isVerified === false ? "var(--error-400-base)" : "grey"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.approveAttendWrap} onClick={() => setIsMakeup(!isMakeup)}>
                                        <span className={styles.checkLabel}>Makeup</span>
                                        <div
                                            className={cx(styles.uncheckedMakeup, {
                                                [styles.checked]: isMakeup
                                            })}>
                                            <Icon icon="TICK" className={styles.attendanceTickIcon} color="#e5e5e5" />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {model.isStaffOrAdmin() ? (
                            <div className={styles.saveContainer}>
                                <SecondaryButton
                                    loading={loading}
                                    disabled={!canDeleteAttendance}
                                    onClick={confirmDeletePopup.open}
                                    title={"Delete"}
                                />
                                <BaseButton loading={loading} onClick={onSave} title={t("popup.save")} />
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className={styles.emptyView}>
                        <span className={styles.textEmpty}>Attendance is empty</span>
                    </div>
                )}
                <BaseLoading isShow={isLoading} />
                <BasePopup
                    width="600px"
                    isShow={userGeolocationMapPopup.isVisible}
                    onClose={userGeolocationMapPopup.close}>
                    {isMapDisplayedAtLeastOnce && (
                        <div style={{padding: 20}}>
                            <UserGeolocationMap geoLocation={geoLocation} />
                        </div>
                    )}
                </BasePopup>

                <ConfirmPopup
                    isVisible={confirmDeletePopup.isVisible}
                    title={"Are you sure you want to delete this attendance?"}
                    onClose={confirmDeletePopup.close}
                    onConfirm={onConfirmDeleteAttendance}
                />
            </div>
        </BasePopup>
    )
}
